import { useFlag } from '@reflag/react-sdk';
import clsx from 'clsx';
import React from 'react';

import * as api from '@gitbook/api-client';
import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    Description,
    EmptyState,
    EmptyStateActions,
    EmptyStateIcon,
    type IconDefinition,
    Pagination,
    Pill,
} from '@gitbook/spine-react';

import { apiResource, useLoggedInUser, usePaginatedAPIQuery } from '../../state';
import { ChangeRequestListCard, ChangeRequestListCardSkeleton } from './ChangeRequestListCard';
import type { ChangeRequestsListFilterState } from './ChangeRequestsListFilters';

export type ChangeRequestsListProps = {
    organizationId: string;

    /** ID or number of the change request to highlight */
    selected?: {
        spaceId: string;
        changeRequestNumber: number;
    };

    /** If provided, only change requests from the space will be displayed */
    spaceId?: string;

    /** Filters to apply to the list of change requests */
    filters: ChangeRequestsListFilterState;

    /**
     * Callback when filters change
     */
    onFiltersChange: (filters: ChangeRequestsListFilterState) => void;

    /**
     * Empty state to display when there are no change requests to show.
     */
    emptyState?: React.ReactNode;

    /**
     * Class name to apply to the list container.
     */
    className?: string;
};

/**
 * List of change requests for an organization or space.
 */
export function ChangeRequestsList(props: ChangeRequestsListProps) {
    const { organizationId, spaceId, selected, filters, onFiltersChange, emptyState, className } =
        props;

    const status = filters?.status ?? api.ChangeRequestStatus.Open;

    const listSpaceId = filters?.space || spaceId;
    const { data, next, previous, range, isLoading } = usePaginatedAPIQuery(
        listSpaceId
            ? apiResource('listChangeRequestsForSpace', {
                  spaceId: listSpaceId,
                  status,
                  contributor: filters?.contributor,
                  creator: filters?.creator,
                  requestedReviewer: filters?.requestedReview,
                  conversationsIssuesCluster: filters?.conversationsIssuesCluster,
              })
            : apiResource('listChangeRequestsForOrganization', {
                  organizationId,
                  status,
                  contributor: filters?.contributor,
                  creator: filters?.creator,
                  requestedReviewer: filters?.requestedReview,
                  conversationsIssuesCluster: filters?.conversationsIssuesCluster,
              })
    );

    return (
        <div className={clsx('flex flex-1 flex-col gap-3', className)}>
            {isLoading || (data && data?.items.length > 0) ? (
                <ChangeRequestsListFiltersSuggestions
                    filters={filters}
                    onFiltersChange={onFiltersChange}
                />
            ) : null}
            {isLoading ? (
                <div
                    role="status"
                    aria-busy
                    className="flex flex-col gap-3 motion-safe:animate-pulse"
                >
                    {Array.from({ length: 3 }).map((_, index) => (
                        <ChangeRequestListCardSkeleton key={index} />
                    ))}
                </div>
            ) : data && data.items.length > 0 ? (
                <>
                    <div className="flex flex-col gap-3">
                        {data?.items.map((item) => (
                            <React.Suspense
                                key={item.id}
                                fallback={<ChangeRequestListCardSkeleton />}
                            >
                                <ChangeRequestListCard
                                    key={item.id}
                                    organizationId={organizationId}
                                    changeRequest={item}
                                    active={
                                        selected?.changeRequestNumber === item.number &&
                                        selected?.spaceId === item.space
                                    }
                                    withSpace={!spaceId}
                                />
                            </React.Suspense>
                        ))}
                    </div>
                    <Pagination next={next} previous={previous} range={range} />
                </>
            ) : (
                (emptyState ?? (
                    <ChangeRequestsListEmptyState
                        filters={filters}
                        onFiltersChange={onFiltersChange}
                    />
                ))
            )}
        </div>
    );
}

/**
 * Suggestions of filters to apply to the change requests list.
 */
export function ChangeRequestsListFiltersSuggestions(props: {
    filters: ChangeRequestsListFilterState;
    onFiltersChange: (filters: ChangeRequestsListFilterState) => void;
}) {
    const { filters, onFiltersChange } = props;
    const currentUser = useLoggedInUser();
    const featureDocsAgent = useFlag('DOCS_AGENTS');

    return (
        <div className="flex gap-1">
            <FilterPill
                label="Yours"
                tooltipLabel="Only show change requests you created"
                filters={filters}
                to={{
                    creator: currentUser.uid,
                }}
                onFiltersChange={onFiltersChange}
            />
            {status === api.ChangeRequestStatus.Open ? (
                <FilterPill
                    label="Awaiting your review"
                    tooltipLabel="Only show change requests awaiting your review"
                    filters={filters}
                    to={{
                        requestedReview: currentUser.uid,
                    }}
                    onFiltersChange={onFiltersChange}
                />
            ) : null}
            {featureDocsAgent.isEnabled ? (
                <FilterPill
                    label="Docs Agent"
                    leadingIcon={Icon.AI}
                    tooltipLabel="Only show change requests created by the Docs Agent"
                    filters={filters}
                    to={{
                        creator: api.GitBookUsers.GitbookAgent,
                    }}
                    onFiltersChange={onFiltersChange}
                />
            ) : null}
        </div>
    );
}

/**
 * Empty state for the change requests list.
 */
export function ChangeRequestsListEmptyState(props: {
    filters: ChangeRequestsListFilterState;
    onFiltersChange: (filters: ChangeRequestsListFilterState) => void;
}) {
    const { filters, onFiltersChange } = props;

    return (
        <div className="flex flex-1 flex-col gap-3">
            <ChangeRequestsListFiltersSuggestions
                filters={filters}
                onFiltersChange={onFiltersChange}
            />
            <EmptyState>
                <EmptyStateIcon>
                    <Icon.ChangeRequest />
                </EmptyStateIcon>
                {filters?.contributor ||
                filters?.space ||
                filters?.creator ||
                filters?.requestedReview ||
                filters?.conversationsIssuesCluster ? (
                    <>
                        <Description>No change requests match the filters.</Description>
                        <EmptyStateActions>
                            <Button
                                kind="link"
                                size="small"
                                label="Clear filters"
                                leadingIcon={Icon.X}
                                onPress={() => {
                                    onFiltersChange({
                                        ...filters,
                                        contributor: undefined,
                                        space: undefined,
                                        creator: undefined,
                                        requestedReview: undefined,
                                        conversationsIssuesCluster: undefined,
                                    });
                                }}
                            />
                        </EmptyStateActions>
                    </>
                ) : filters?.status === api.ChangeRequestStatus.Open ? (
                    <Description>All good! No change requests to review.</Description>
                ) : filters?.status ? (
                    <Description>No {filters?.status} change requests to show.</Description>
                ) : (
                    <Description>No change requests to show.</Description>
                )}
            </EmptyState>
        </div>
    );
}

/**
 * Pill button to toggle quick filters.
 */
function FilterPill(props: {
    label: string;
    tooltipLabel: string;
    leadingIcon?: IconDefinition;
    filters: ChangeRequestsListFilterState;
    to: ChangeRequestsListFilterState;
    onFiltersChange: (filters: ChangeRequestsListFilterState) => void;
}) {
    const { to, filters, label, leadingIcon, tooltipLabel, onFiltersChange } = props;
    const active = Object.entries(to).every(([key, value]) => filters?.[key] === value);

    const onPress = () => {
        onFiltersChange(
            active
                ? {
                      ...filters,
                      // Unset all properties defined in this filter
                      ...Object.fromEntries(Object.entries(to).map(([key]) => [key, undefined])),
                  }
                : {
                      ...filters,
                      // Set all properties defined in this filter
                      ...to,
                  }
        );
    };

    return (
        <Pill
            label={label}
            leadingIcon={leadingIcon}
            active={active}
            onPress={onPress}
            tooltip={{ label: tooltipLabel }}
        />
    );
}
